import { getContentstackImagesForWidths } from '@noths/polaris-client-utils';
import dynamic from 'next/dynamic';

import { YouTubeVideoBlockProps } from 'src/components/blocks/YouTubeVideoBlockBlock';
import {
  trackYouTubeVideoClick,
  trackYouTubeVideoImpression,
} from '../../../tracking/components/videoBlock';
import { ContentBlockRenderer } from '../../../types/Block';
import { ContentstackImage, ContentstackSection } from '../../../types/Contentstack';
import { Transformer } from '../../../types/Transformer';

export interface ContentstackVideoBlock extends ContentstackSection {
  youtube_id: string;
  thumbnail?: ContentstackImage;
  title?: string;
  caption?: string;
  tracking_title: string;
}

type VideoBlockSerializedData = Omit<YouTubeVideoBlockProps, 'onClick' | 'onVisible'> & {
  trackingTitle: string;
};

/* istanbul ignore next */
export const renderer: ContentBlockRenderer<VideoBlockSerializedData, YouTubeVideoBlockProps> = {
  Component: dynamic<YouTubeVideoBlockProps>(() =>
    import('src/components/blocks/YouTubeVideoBlockBlock').then(
      (mod) => mod.YouTubeVideoBlockBlock,
    ),
  ),
  getProps: (serializedData) => {
    const { trackingPosition, trackingTitle, title, ...restOfSerializedData } = serializedData;
    const placementContext = {
      trackingPosition,
      trackingTitle,
      videoTitle: title || '',
    };
    return {
      onVideoClick: () => trackYouTubeVideoClick(placementContext),
      onVisible: () => trackYouTubeVideoImpression(placementContext),
      title,
      ...restOfSerializedData,
    };
  },
};

export const transformer: Transformer<
  ContentstackVideoBlock,
  VideoBlockSerializedData,
  YouTubeVideoBlockProps
> = ({ _metadata, youtube_id, caption = '', title = '', thumbnail, tracking_title }) => ({
  uid: _metadata.uid,
  renderer,
  serializedData: {
    youtubeId: youtube_id,
    ...(thumbnail
      ? {
          thumbnailImages: getContentstackImagesForWidths(
            thumbnail.url,
            [360, 500, 700, 900, 1132],
            {
              aspectRatio: '16:9',
            },
          ),
          thumbnailWidth: 360,
          thumbnailHeight: 216,
        }
      : { thumbnailImages: [] }),
    title,
    caption,
    trackingTitle: tracking_title,
  },
});
